<template>
  <section class="form-list" v-loading="isLoading">
    <el-card shadow="never" class="detail-card">
      <div slot="header">入职申请 #{{ apply.id }}</div>

      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-position="right"
        label-width="130px"
        class="detail-form"
      >
        <el-form-item label="昵称">{{ apply.nickname }}</el-form-item>
        <el-form-item label="用户名">{{ apply.username }}</el-form-item>
        <el-form-item label="手机号">{{ apply.phone || '未填写' }}</el-form-item>
        <el-form-item label="企业微信绑定">{{ apply.we_chat || '通过后按手机号自动邀请绑定' }}</el-form-item>
        <el-form-item label="门店">{{ apply.store_name | formatStoreName }}</el-form-item>
        <el-form-item label="职位">{{ apply.role_name }}</el-form-item>
        <el-form-item label="状态">
          <el-tag :type="stateTag">{{ stateName }}</el-tag>
        </el-form-item>
        <el-form-item label="提交时间">{{ apply.add_time | timeToYmdHi }}</el-form-item>

        <template v-if="isReviewed">
          <el-form-item :label="auditTimeLabel">{{ apply.update_time | timeToYmdHi }}</el-form-item>
          <el-form-item label="审核人">
            <v-avatar-nickname
              v-if="apply.update_administrator_id"
              :value="apply.admin_avatar_update"
              :nickname="apply.admin_nickname_update || ('#' + apply.update_administrator_id)"
              :avatar-size="28"
              :nickname-overflow="true"
              width="180px"
            />
            <span v-else>未记录</span>
          </el-form-item>
          <el-form-item
            v-if="apply.remark"
            :label="Number(apply.apply_state) === Number(applyStates.rejected.value) ? '驳回原因' : '审核备注'"
          >
            <span class="detail-remark">{{ apply.remark }}</span>
          </el-form-item>
          <el-form-item v-if="Number(apply.apply_state) === Number(applyStates.rejected.value)" label="重新申请">
            <div class="reject-hint">
              <div class="reject-hint-title">该申请已驳回，员工可重新填写申请</div>
              <div class="reject-hint-text">若邀请链接仍在有效期内，无需重新生成邀请链接；过期后需重新生成。</div>
              <div class="reject-hint-text">如不希望对方继续申请，请到「入职邀请」列表撤销该邀请，撤销后对方将无法再提交入职申请。</div>
            </div>
          </el-form-item>
        </template>

        <template v-if="apply.apply_state === applyStates.review.value">
          <el-form-item v-if="historyAccount.id" label="历史账号">
            <div class="history-hint">
              <div class="history-hint-title">
                该手机号存在已离职的历史账号：{{ historyAccount.nickname }}（{{ historyAccount.username }}）
              </div>
              <div class="history-hint-text">离职/注销时间：{{ historyAccountTime | timeToYmdHi }}</div>
              <div class="history-hint-text">
                通过后将创建全新账号，历史任职与薪资档案仍留在原账号上；若需延续原账号档案，请驳回本申请，
                再到「离职记录」中还原原账号并办理【重新入职】。
              </div>
            </div>
          </el-form-item>
          <el-form-item label="底薪"><v-input-money class="salary-input" :max="99999.99" :is-show-suffix="true" v-model="salary.base_pay"/></el-form-item>
          <el-form-item label="公司社保"><v-input-money class="salary-input" :max="99999.99" :is-show-suffix="true" v-model="salary.social_insurance"/></el-form-item>
          <el-form-item label="门店社保"><v-input-money class="salary-input" :max="99999.99" :is-show-suffix="true" v-model="salary.store_insurance"/></el-form-item>
          <el-form-item label="个人社保"><v-input-money class="salary-input" :max="99999.99" :is-show-suffix="true" v-model="salary.personal_insurance"/></el-form-item>
          <el-form-item label="业绩任务"><v-input-money class="salary-input" :max="999999.99" :is-show-suffix="true" v-model="salary.task_income"/></el-form-item>
          <el-form-item label="底薪比例"><v-input-money class="rate-input" :min="0" :max="100" :is-show-suffix="true" :is-small-suffix="true" suffix-name="%" v-model="salary.base_pay_rate"/></el-form-item>
          <el-form-item label="提成比例"><v-input-money class="rate-input" :min="0" :max="100" :is-show-suffix="true" :is-small-suffix="true" suffix-name="%" v-model="salary.commission_rate"/></el-form-item>
          <el-form-item label="薪资说明">
            <span class="detail-help">薪资选填；不填也可在建立账号后再补充。</span>
          </el-form-item>

          <el-form-item label="审核结果">
            <el-radio-group v-model="isReject" @change="handleAuditModeChange">
              <el-radio :label="0">通过入职</el-radio>
              <el-radio :label="1">驳回</el-radio>
            </el-radio-group>
            <div v-if="isReject" class="reject-hint">
              <div class="reject-hint-title">驳回后员工仍可重新填写申请</div>
              <div class="reject-hint-text">若邀请链接仍在有效期内，员工可重新填写申请，无需重新生成邀请链接；过期后需重新生成。</div>
              <div class="reject-hint-text">如不希望对方继续申请，请到「入职邀请」列表撤销该邀请，撤销后对方将无法再提交入职申请。</div>
            </div>
          </el-form-item>
          <el-form-item :label="isReject ? '驳回原因' : '审核备注'" prop="remark" :required="isReject">
            <el-input
              class="remark-input"
              v-model="form.remark"
              type="textarea"
              :rows="2"
              maxlength="200"
              show-word-limit
              clearable
              :placeholder="isReject ? '请输入驳回原因（必填）' : '选填，可填写审核说明'"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="isSaving" @click="handleExamine">
              {{ isReject ? '驳回申请' : '通过入职' }}
            </el-button>
            <el-button @click="back">返回</el-button>
          </el-form-item>
        </template>

        <el-form-item v-else>
          <el-button @click="back">返回</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </section>
</template>

<script>
import { applyStates } from '@/enums/applyStates';
import RouteConfig from '@/router/config';
import { toCent, toYuan } from '@/filters';
import { onboardApplyShow, onboardApplyReview } from '@/api/personnel';

export default {
  name: RouteConfig.administratorOnboardApplyDetail.name,
  data: () => ({
    RouteConfig,
    applyStates,
    isLoading: false,
    isSaving: false,
    isReject: 0,
    apply: {},
    form: {
      remark: '',
    },
    salary: {
      base_pay: 0,
      social_insurance: 0,
      store_insurance: 0,
      personal_insurance: 0,
      task_income: 0,
      base_pay_rate: 0,
      commission_rate: 0,
    },
  }),
  computed: {
    // 后端在待审核详情中返回的同手机号已离职/已注销历史账号（无历史账号时为 {}）
    historyAccount() {
      return this.apply.history_account || {};
    },
    historyAccountTime() {
      return Number(this.historyAccount.leave_time) || Number(this.historyAccount.is_del) || 0;
    },
    stateName() {
      for (const key in applyStates) {
        if (Number(applyStates[key].value) === Number(this.apply.apply_state)) return applyStates[key].name;
      }
      return '';
    },
    stateTag() {
      for (const key in applyStates) {
        if (Number(applyStates[key].value) === Number(this.apply.apply_state)) return applyStates[key].tagType || 'info';
      }
      return 'info';
    },
    isReviewed() {
      return Number(this.apply.apply_state) === Number(applyStates.passed.value)
        || Number(this.apply.apply_state) === Number(applyStates.rejected.value);
    },
    auditTimeLabel() {
      return Number(this.apply.apply_state) === Number(applyStates.passed.value) ? '通过时间' : '驳回时间';
    },
    rules() {
      if (this.apply.apply_state !== applyStates.review.value || !this.isReject) return {};
      return {
        remark: [{
          validator: (rule, value, callback) => {
            if (!value || !String(value).trim()) {
              callback(new Error('请输入驳回原因'));
              return;
            }
            if (String(value).trim().length < 2) {
              callback(new Error('请输入至少 2 个字符'));
              return;
            }
            callback();
          },
          trigger: 'blur',
        }],
      };
    },
  },
  async mounted() {
    this.isLoading = true;
    const { isError, data } = await onboardApplyShow(this.$route.params.id);
    this.isLoading = false;
    if (isError) return;
    this.apply = data;
    this.form.remark = data.remark || '';
    this.salary = {
      base_pay: toYuan(data.base_pay),
      social_insurance: toYuan(data.social_insurance),
      store_insurance: toYuan(data.store_insurance),
      personal_insurance: toYuan(data.personal_insurance),
      task_income: toYuan(data.task_income),
      base_pay_rate: toYuan(data.base_pay_rate),
      commission_rate: toYuan(data.commission_rate),
    };
  },
  methods: {
    handleAuditModeChange() {
      this.$refs.form && this.$refs.form.clearValidate('remark');
    },
    handleExamine() {
      if (this.isSaving) return;
      if (this.isReject) {
        this.$refs.form.validate((isSuccess) => {
          if (!isSuccess) return;
          this.doReview(false);
        });
        return;
      }
      this.pass();
    },
    async doReview(isPassed) {
      this.isSaving = true;
      const data = {
        is_passed: isPassed ? 1 : 0,
        remark: this.form.remark,
      };
      if (isPassed) {
        data.base_pay = toCent(this.salary.base_pay);
        data.social_insurance = toCent(this.salary.social_insurance);
        data.store_insurance = toCent(this.salary.store_insurance);
        data.personal_insurance = toCent(this.salary.personal_insurance);
        data.task_income = toCent(this.salary.task_income);
        data.base_pay_rate = toCent(this.salary.base_pay_rate);
        data.commission_rate = toCent(this.salary.commission_rate);
      }
      const { isError } = await onboardApplyReview(this.apply.id, data);
      this.isSaving = false;
      if (isError) return;
      this.$message.success(isPassed ? '已通过入职' : '已驳回');
      this.back();
    },
    pass() {
      // 手机号命中已离职历史账号时，二次确认里再提醒一次：通过即新建账号，历史档案留在原账号
      const message = this.historyAccount.id
        ? '该手机号存在已离职的历史账号，通过后将为其创建全新账号，历史任职与薪资档案仍留在原账号。确认通过该入职申请吗？'
        : '确认通过该入职申请吗？通过后将直接创建账号，薪资以当前填写为准。';
      this.$confirm(message, '提示', { type: 'warning' })
        .then(() => this.doReview(true)).catch(() => {});
    },
    back() {
      RouteConfig.administratorOnboardApplyList.push();
    },
  },
};
</script>

<style scoped>
.detail-card { max-width: 720px; }
.detail-form { max-width: 720px; }
.salary-input { width: 150px; }
.rate-input { width: 90px; }
.remark-input { max-width: 520px; }
.detail-help { color: #909399; font-size: 13px; line-height: 1.6; }
.detail-remark { color: #606266; line-height: 1.6; word-break: break-all; }
.reject-hint { margin-top: 8px; padding: 10px 12px; background: #fef0f0; border: 1px solid #fde2e2; border-radius: 4px; color: #f56c6c; font-size: 13px; line-height: 1.6; }
.reject-hint-title { font-weight: 600; margin-bottom: 4px; }
.reject-hint-text { color: #f56c6c; }
.history-hint { line-height: 1.6; font-size: 13px; color: #e6a230; background: #fdf6ec; border: 1px solid #faecd8; border-radius: 4px; padding: 10px 12px; }
.history-hint-title { font-weight: 600; margin-bottom: 4px; }
.history-hint-text { color: #e6a230; }
</style>
